import React from 'react';
import { Box, Checkbox, Chip, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CommonModalButton from '../../../form-components/CommonModalButton';

const PRIMARY = '#4AB3BC';
const PRIMARY_DARK = '#0F7F85';

/**
 * 출입문 다중 선택 모달 (#91) — 판매지점 선택(BranchSelectModal)과 같은 체크박스 패턴.
 * 출입 제한이 없는 문은 모든 상품이 항상 지나가므로 체크 고정(해제 불가)으로 보여주고,
 * 제한 문만 켜고 끈다. 상품 폼과 회원 상품 카드가 함께 쓴다.
 *
 * doors: [{ id, doorName, restricted, badge? }]
 * selectedIds: 제한 문 중 선택된 id 목록 (미제한 문 id 는 포함하지 않는다)
 * onConfirm: 있으면 '선택 완료'가 이걸 부른다(회원 카드처럼 저장이 따로 필요한 경우). 없으면 닫기만.
 */
const DoorSelectModal = ({
  open,
  title,
  subtitle,
  doors = [],
  selectedIds = [],
  onChangeIds,
  onClose,
  onConfirm,
  confirmLabel,
  confirming = false,
  embedded = false,
}) => {
  const restrictedDoors = doors.filter(d => d.restricted);
  const selectedCount = selectedIds.length;
  const totalCount = restrictedDoors.length;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  const toggleDoor = id => {
    onChangeIds(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
  };

  const toggleAll = () => {
    onChangeIds(allSelected ? [] : restrictedDoors.map(d => d.id));
  };

  const inner = (
      <Box
        sx={
          embedded
            ? { width: '100%', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden', border: '1px solid #eee' }
            : {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '440px',
                backgroundColor: '#fff',
                borderRadius: '10px',
                overflow: 'hidden',
              }
        }>
        {!embedded && (
          <Box
            sx={{
              backgroundColor: '#555',
              height: '52px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: '10px 10px 0 0',
              padding: '0 20px',
            }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>{title}</Typography>
            <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
          </Box>
        )}

        {subtitle && (
          <Box sx={{ p: '10px 16px', backgroundColor: '#fafafa', borderBottom: '1px solid #eee' }}>
            <Typography sx={{ fontSize: '12px', color: '#777', lineHeight: 1.6 }}>{subtitle}</Typography>
          </Box>
        )}

        {/* 선택 요약 — 상품 폼 요약 박스와 동일. 제한 문은 x 로 바로 제외, 항상 가능 문은 고정 */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            alignItems: 'center',
            minHeight: 44,
            px: '16px',
            py: '8px',
            borderBottom: '1px solid #eee',
            backgroundColor: '#F7F7F7',
          }}>
          {restrictedDoors
            .filter(d => selectedIds.includes(d.id))
            .map(d => (
              <Box
                key={d.id}
                sx={{
                  pl: '10px',
                  pr: '6px',
                  height: 26,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  borderRadius: '13px',
                  background: PRIMARY,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                {d.doorName}
                <Box
                  component="button"
                  type="button"
                  aria-label={`${d.doorName} 제외`}
                  onClick={() => toggleDoor(d.id)}
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: 'none',
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.25)',
                    color: '#fff',
                    cursor: 'pointer',
                    '&:hover': { background: 'rgba(255,255,255,0.45)' },
                  }}>
                  <CloseIcon sx={{ fontSize: 12 }} />
                </Box>
              </Box>
            ))}
          {doors
            .filter(d => !d.restricted)
            .map(d => (
              <Box
                key={d.id}
                sx={{
                  px: '10px',
                  height: 26,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  borderRadius: '13px',
                  background: '#E3F2F3',
                  color: PRIMARY_DARK,
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                {d.doorName}
                <Box component="span" sx={{ fontSize: 10, opacity: 0.8 }}>항상 가능</Box>
              </Box>
            ))}
        </Box>

        {/* 전체 선택 — 제한 문 기준 */}
        {totalCount > 0 && (
          <Box
            onClick={toggleAll}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: '16px',
              py: '10px',
              cursor: 'pointer',
              borderBottom: '1px solid #eee',
              backgroundColor: allSelected ? '#e8f5f5' : '#fff',
              '&:hover': { backgroundColor: allSelected ? '#ddf0f0' : '#f5f5f5' },
            }}>
            <Checkbox
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              size="small"
              sx={{
                p: 0,
                mr: 1.5,
                color: PRIMARY,
                '&.Mui-checked': { color: PRIMARY },
                '&.MuiCheckbox-indeterminate': { color: PRIMARY },
              }}
            />
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>전체 선택</Typography>
            <Typography sx={{ fontSize: '13px', color: PRIMARY, fontWeight: 500, ml: 'auto' }}>
              {selectedCount}/{totalCount}
            </Typography>
          </Box>
        )}

        {/* 출입문 목록 */}
        <Box
          sx={{
            maxHeight: '360px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
          }}>
          {doors.length === 0 ? (
            <Typography sx={{ p: '16px', fontSize: '13px', color: '#999', textAlign: 'center' }}>
              등록된 출입문이 없습니다
            </Typography>
          ) : (
            doors.map(door => {
              const fixed = !door.restricted;
              const isChecked = fixed || selectedIds.includes(door.id);
              return (
                <Box
                  key={door.id}
                  onClick={fixed ? undefined : () => toggleDoor(door.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: '16px',
                    py: '8px',
                    cursor: fixed ? 'default' : 'pointer',
                    backgroundColor: isChecked ? '#f0f8f8' : 'transparent',
                    borderLeft: isChecked ? `3px solid ${PRIMARY}` : '3px solid transparent',
                    borderBottom: '1px solid #f0f0f0',
                    opacity: fixed ? 0.75 : 1,
                    transition: 'all 0.1s ease',
                    '&:hover': fixed ? undefined : { backgroundColor: isChecked ? '#e8f5f5' : '#f8f8f8' },
                  }}>
                  <Checkbox
                    checked={isChecked}
                    disabled={fixed}
                    size="small"
                    sx={{
                      p: 0,
                      mr: 1.5,
                      color: '#c4c4c4',
                      '&.Mui-checked': { color: PRIMARY },
                      '&.Mui-disabled.Mui-checked': { color: PRIMARY },
                    }}
                  />
                  <Typography sx={{ fontSize: '14px', color: isChecked ? '#333' : '#555' }}>
                    {door.doorName}
                  </Typography>
                  {fixed ? (
                    <Chip
                      label="항상 가능"
                      size="small"
                      sx={{ ml: 'auto', height: 20, fontSize: 11, fontWeight: 600, color: PRIMARY_DARK, background: '#E3F2F3' }}
                    />
                  ) : (
                    door.badge && (
                      <Chip
                        label={door.badge}
                        size="small"
                        sx={{ ml: 'auto', height: 20, fontSize: 11, fontWeight: 600, color: '#737373', background: '#F5F5F5' }}
                      />
                    )
                  )}
                </Box>
              );
            })
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            padding: '14px 20px',
            borderTop: '1px solid #eee',
            backgroundColor: '#fafafa',
          }}>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={onConfirm ?? onClose} disabled={confirming}>
            {confirmLabel ?? `선택 완료 (${selectedCount}개)`}
          </CommonModalButton>
        </Box>
      </Box>
  );

  if (embedded) {
    return inner;
  }
  return (
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      {inner}
    </Modal>
  );
};

export default DoorSelectModal;
